import Phaser from 'phaser';
import { LOGICAL_WIDTH, LOGICAL_HEIGHT, RENDER_SCALE } from '../dimensions';
import { audio } from '../game/Audio';

const PHASE_MS = 700;
const SEGMENTS = 28;
const WAVE_AMP = 12;
const JITTER_AMP = 7;

// Emission rates per second, scaled by deltaMs each update.
const SMOKE_PER_SEC = 320;
const EMBER_PER_SEC = 120;
const SMOKE_TEX = 'burn-smoke';
const EMBER_TEX = 'burn-ember';

type Phase = 'idle' | 'cover' | 'hold' | 'reveal';

// Hold the fully-covered black frame for N updates after the midpoint callback so any
// long-running scene setup (heavy texture renders, etc.) finishes inside the hold window.
// The user perceives this as part of the burn — the freeze is invisible against full black.
const HOLD_UPDATES = 3;

const HOLD_TEXT_FADE_MS = 200;
const HOLD_TEXT_VISIBLE_MS = 1400;
const HOLD_TEXT_TOTAL_MS = HOLD_TEXT_FADE_MS * 2 + HOLD_TEXT_VISIBLE_MS;

const FRAGMENT_SHADER = /* glsl */ `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform vec2  uResolution;
uniform float uBurnY;
uniform float uPhase;     // 0 = cover (char below line), 1 = reveal (char above line)
uniform float uSeed;
uniform float uTimeMs;
uniform vec3  uColorChar;
uniform vec3  uColorGlow;

varying vec2 outTexCoord;

const float PI = 3.14159265;
const float WAVE_AMP = 12.0;
const float JITTER_AMP = 7.0;
const float GLOW_H = 26.0;

void main () {
  vec2 fragPx = vec2(outTexCoord.x * uResolution.x, (1.0 - outTexCoord.y) * uResolution.y);
  float u = fragPx.x / uResolution.x;

  float wave   = sin(u * PI * 3.4 + uSeed) * WAVE_AMP;
  float jitter = sin(u * PI * 11.0 + uTimeMs * 0.013 + uSeed * 0.7) * JITTER_AMP;
  float yLine  = uBurnY + wave + jitter;

  // side = +1 for cover (char below line), -1 for reveal (char above line).
  // dChar > 0 means "on the char side of the line".
  float side  = (uPhase < 0.5) ? 1.0 : -1.0;
  float dChar = (fragPx.y - yLine) * side;

  if (dChar < 0.0) discard;

  // Glow band hugs the line on the char side; intensity fades from line into char.
  // Approximates the original "char (opaque black) + glow polygon (0.55 alpha)" overlay.
  float band = clamp(1.0 - dChar / GLOW_H, 0.0, 1.0);
  vec3 col = mix(uColorChar, uColorGlow, band * 0.55);
  gl_FragColor = vec4(col, 1.0);
}
`;

export type BurnTransitionPayload = {
  onMidpoint: () => void;
  onComplete?: () => void;
  holdText?: { title: string; subtitle?: string };
};

export const BURN_EVENT = 'lavascend:burn';

export class BurnTransitionScene extends Phaser.Scene {
  private cover!: Phaser.GameObjects.Rectangle;
  private burnShader?: Phaser.GameObjects.Shader;
  private inputBlocker!: Phaser.GameObjects.Rectangle;
  private smoke!: Phaser.GameObjects.Particles.ParticleEmitter;
  private embers!: Phaser.GameObjects.Particles.ParticleEmitter;
  private phase: Phase = 'idle';
  private startedAt = 0;
  private lastUpdate = 0;
  private smokeAccrual = 0;
  private emberAccrual = 0;
  private holdLeft = 0;
  private onMidpoint?: () => void;
  private onComplete?: () => void;
  private seed = 0;
  private holdTextObjs: Phaser.GameObjects.Text[] = [];
  private holdTextMs = 0;
  private holdTextActive = false;

  constructor() { super({ key: 'BurnTransition', active: true }); }

  isPlaying(): boolean { return this.phase !== 'idle'; }

  create(): void {
    this.cameras.main.setOrigin(0, 0).setZoom(RENDER_SCALE);
    this.ensureParticleTextures();

    this.cover = this.add.rectangle(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT, 0x000000, 1)
      .setOrigin(0, 0).setScrollFactor(0).setDepth(10000).setVisible(false);

    if (this.sys.renderer.type === Phaser.WEBGL) {
      this.burnShader = this.add.shader({
        name: 'burn-line',
        fragmentSource: FRAGMENT_SHADER,
        initialUniforms: {
          uResolution: new Float32Array([LOGICAL_WIDTH, LOGICAL_HEIGHT]),
          uBurnY:    LOGICAL_HEIGHT + 40,
          uPhase:    0,
          uSeed:     0,
          uTimeMs:   0,
          uColorChar: new Float32Array([0, 0, 0]),
          uColorGlow: new Float32Array([0x6a / 255, 0x1a / 255, 0x00 / 255]),
        },
      }, 0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT)
        .setOrigin(0, 0)
        .setScrollFactor(0)
        .setDepth(10001)
        .setVisible(false);
    }

    // Smoke: soft, large, drifts up, warm ember tones at birth fading to light grey wisps
    // that stay readable against both the unburned scene and the black char.
    this.smoke = this.add.particles(0, 0, SMOKE_TEX, {
      frequency: -1,
      lifespan: { min: 900, max: 1500 },
      speedY: { min: -200, max: -110 },
      speedX: { min: -34, max: 34 },
      accelerationY: -40,
      scale: { start: 0.4, end: 2.0, ease: 'Sine.easeOut' },
      alpha: { start: 0.65, end: 0, ease: 'Cubic.easeOut' },
      tint: [0xffb37a, 0xa07a78, 0x807a82, 0x6a6770, 0x4a4750],
      rotate: { min: -25, max: 25 },
      blendMode: Phaser.BlendModes.NORMAL,
    }).setScrollFactor(0).setDepth(10002);

    // Embers: tiny bright sparks at the fire edge, fast rise, short life, additive glow.
    this.embers = this.add.particles(0, 0, EMBER_TEX, {
      frequency: -1,
      lifespan: { min: 400, max: 900 },
      speedY: { min: -260, max: -140 },
      speedX: { min: -40, max: 40 },
      accelerationY: -90,
      scale: { start: 1, end: 0.05, ease: 'Cubic.easeOut' },
      alpha: { start: 0.95, end: 0, ease: 'Cubic.easeOut' },
      tint: [0xffe9a3, 0xffb35a, 0xff6a1a],
      blendMode: Phaser.BlendModes.ADD,
    }).setScrollFactor(0).setDepth(10003);

    this.inputBlocker = this.add.rectangle(
      LOGICAL_WIDTH / 2, LOGICAL_HEIGHT / 2, LOGICAL_WIDTH, LOGICAL_HEIGHT, 0x000000, 0
    ).setScrollFactor(0).setDepth(9999).setInteractive().setVisible(false);

    this.scene.bringToTop();

    this.game.events.on(BURN_EVENT, (payload: BurnTransitionPayload) => this.play(payload));
    this.events.once('shutdown', () => this.game.events.off(BURN_EVENT));
  }

  private play({ onMidpoint, onComplete, holdText }: BurnTransitionPayload): void {
    if (this.phase !== 'idle') return;
    this.phase = 'cover';
    audio.burn();
    this.startedAt = this.time.now;
    this.lastUpdate = this.time.now;
    this.smokeAccrual = 0;
    this.emberAccrual = 0;
    this.onMidpoint = onMidpoint;
    this.onComplete = onComplete;
    this.seed = Math.random() * 1000;
    this.inputBlocker.setVisible(true);
    this.scene.bringToTop();
    this.holdTextActive = !!holdText;
    this.holdTextMs = 0;
    if (holdText) this.queueHoldText(holdText);
  }

  update(): void {
    if (this.phase === 'idle') {
      this.cover.setVisible(false);
      this.burnShader?.setVisible(false);
      return;
    }

    if (this.phase === 'hold') {
      this.drawFullCover();
      const now = this.time.now;
      const dt = Math.min(now - this.lastUpdate, 80);
      this.lastUpdate = now;
      this.tickHoldText(dt);
      // Hold until both the fixed frame count AND the text-show animation finish.
      const textDone = !this.holdTextActive || this.holdTextMs >= HOLD_TEXT_TOTAL_MS;
      this.holdLeft--;
      if (this.holdLeft <= 0 && textDone) {
        this.phase = 'reveal';
        this.startedAt = this.time.now;
        this.lastUpdate = this.time.now;
        this.cover.setVisible(false);
        this.burnShader?.setVisible(true);
      }
      return;
    }

    const now = this.time.now;
    const dt = Math.min(now - this.lastUpdate, 80);
    this.lastUpdate = now;

    const t = Phaser.Math.Clamp((now - this.startedAt) / PHASE_MS, 0, 1);
    const eased = Phaser.Math.Easing.Quadratic.InOut(t);

    // Burn line travels from below the screen to above it, leaving margin so
    // particles emitted before/after burn are off-screen and clean.
    const startY = LOGICAL_HEIGHT + 40;
    const endY = -40;
    const burnY = Phaser.Math.Linear(startY, endY, eased);

    const ys = this.drawCharLine(burnY, now);
    this.spawnParticles(ys, dt);

    if (t < 1) return;

    if (this.phase === 'cover') {
      // Final cover frame is already drawn (full black). Kill in-flight particles before
      // the freeze — otherwise they hang motionless over the black screen while the main
      // thread is blocked and the whole transition looks frozen.
      this.smoke.killAll();
      this.embers.killAll();
      this.drawFullCover();
      const cb = this.onMidpoint;
      this.onMidpoint = undefined;
      this.phase = 'hold';
      this.holdLeft = HOLD_UPDATES;
      cb?.();
      return;
    }

    const done = this.onComplete;
    this.onComplete = undefined;
    this.phase = 'idle';
    this.inputBlocker.setVisible(false);
    this.cover.setVisible(false);
    this.burnShader?.setVisible(false);
    this.smoke.killAll();
    this.embers.killAll();
    this.disposeHoldText();
    done?.();
  }

  private drawFullCover(): void {
    this.burnShader?.setVisible(false);
    this.cover.setVisible(true);
  }

  private drawCharLine(burnY: number, now: number): number[] {
    const ys: number[] = new Array(SEGMENTS + 1);
    for (let i = 0; i <= SEGMENTS; i++) {
      const u = i / SEGMENTS;
      const wave = Math.sin(u * Math.PI * 3.4 + this.seed) * WAVE_AMP;
      const jitter = Math.sin(u * Math.PI * 11 + now * 0.013 + this.seed * 0.7) * JITTER_AMP;
      ys[i] = burnY + wave + jitter;
    }

    if (this.burnShader) {
      this.burnShader.setVisible(true);
      this.cover.setVisible(false);
      this.burnShader.setUniform('uBurnY', burnY);
      this.burnShader.setUniform('uPhase', this.phase === 'cover' ? 0 : 1);
      this.burnShader.setUniform('uSeed', this.seed);
      this.burnShader.setUniform('uTimeMs', now);
    } else {
      // Canvas-renderer fallback: cover Rectangle alone — same shape as Background.ts's fallback.
      this.cover.setVisible(true);
    }

    return ys;
  }

  private spawnParticles(ys: number[], dtMs: number): void {
    // Frame-rate-independent accrual so emit rate stays steady across devices.
    this.smokeAccrual += (SMOKE_PER_SEC * dtMs) / 1000;
    this.emberAccrual += (EMBER_PER_SEC * dtMs) / 1000;
    const smokeN = Math.floor(this.smokeAccrual);
    const emberN = Math.floor(this.emberAccrual);
    this.smokeAccrual -= smokeN;
    this.emberAccrual -= emberN;

    for (let i = 0; i < smokeN; i++) {
      const x = Math.random() * LOGICAL_WIDTH;
      const seg = Math.min(SEGMENTS, Math.max(0, Math.floor((x / LOGICAL_WIDTH) * SEGMENTS)));
      this.smoke.emitParticleAt(x, ys[seg] + (Math.random() - 0.5) * 4, 1);
    }
    for (let i = 0; i < emberN; i++) {
      const x = Math.random() * LOGICAL_WIDTH;
      const seg = Math.min(SEGMENTS, Math.max(0, Math.floor((x / LOGICAL_WIDTH) * SEGMENTS)));
      this.embers.emitParticleAt(x, ys[seg] + (Math.random() - 0.5) * 6, 1);
    }
  }

  private queueHoldText(holdText: { title: string; subtitle?: string }): void {
    this.disposeHoldText();
    const cx = LOGICAL_WIDTH / 2;
    const titleY = LOGICAL_HEIGHT * 0.45;
    const subY = LOGICAL_HEIGHT * 0.55;
    const title = this.add.text(cx, titleY, holdText.title, {
      fontFamily: '"Bungee Inline", "Impact", sans-serif',
      fontSize: '40px',
      color: '#FFE8D6',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(10010).setAlpha(0);
    this.holdTextObjs.push(title);
    if (holdText.subtitle) {
      const sub = this.add.text(cx, subY, holdText.subtitle, {
        fontFamily: '"Familjen Grotesk", system-ui, sans-serif',
        fontSize: '18px',
        color: '#B89BB8',
      }).setOrigin(0.5).setScrollFactor(0).setDepth(10010).setAlpha(0);
      this.holdTextObjs.push(sub);
    }
  }

  private tickHoldText(dtMs: number): void {
    if (!this.holdTextActive || this.holdTextObjs.length === 0) return;
    this.holdTextMs += dtMs;
    const t = this.holdTextMs;
    let alpha = 0;
    if (t < HOLD_TEXT_FADE_MS) {
      alpha = t / HOLD_TEXT_FADE_MS;
    } else if (t < HOLD_TEXT_FADE_MS + HOLD_TEXT_VISIBLE_MS) {
      alpha = 1;
    } else if (t < HOLD_TEXT_TOTAL_MS) {
      alpha = 1 - (t - HOLD_TEXT_FADE_MS - HOLD_TEXT_VISIBLE_MS) / HOLD_TEXT_FADE_MS;
    } else {
      alpha = 0;
    }
    for (const o of this.holdTextObjs) o.setAlpha(alpha);
  }

  private disposeHoldText(): void {
    for (const o of this.holdTextObjs) o.destroy();
    this.holdTextObjs = [];
    this.holdTextActive = false;
    this.holdTextMs = 0;
  }

  private ensureParticleTextures(): void {
    if (!this.textures.exists(SMOKE_TEX)) {
      const size = 64;
      const ct = this.textures.createCanvas(SMOKE_TEX, size, size);
      if (ct) {
        const ctx = ct.getContext();
        const grd = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
        grd.addColorStop(0.0, 'rgba(255,255,255,1.0)');
        grd.addColorStop(0.4, 'rgba(255,255,255,0.55)');
        grd.addColorStop(1.0, 'rgba(255,255,255,0.0)');
        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, size, size);
        ct.refresh();
      }
    }
    if (!this.textures.exists(EMBER_TEX)) {
      const size = 16;
      const ct = this.textures.createCanvas(EMBER_TEX, size, size);
      if (ct) {
        const ctx = ct.getContext();
        const grd = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
        grd.addColorStop(0.0, 'rgba(255,255,255,1.0)');
        grd.addColorStop(0.5, 'rgba(255,255,255,0.7)');
        grd.addColorStop(1.0, 'rgba(255,255,255,0.0)');
        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, size, size);
        ct.refresh();
      }
    }
  }
}
